import { useCallback, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ChevronRight,
  ContentCopy,
  ExpandMore,
  ExpandLess,
  History,
  Inventory2,
  OpenInNew,
  Refresh,
  AutoFixHigh,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Pagination,
  Tooltip,
  Typography,
} from '@mui/material';

import { AppHeader } from '../components/app-header';
import { LoadingState } from '../components/loading-state';
import {
  useGetContainerComponentDetailQuery,
  useGetContainerEntityDetailQuery,
  useGetProviderInfoQuery,
  useRegeneratePatientIntelligenceMutation,
} from '../store/hooks';
import { formatDateTime } from '../utils/formatting';

function ComponentHistory({
  containerKey,
  entityId,
  typeKey,
  userId,
  organizationId,
}: {
  containerKey: string;
  entityId: string;
  typeKey: string;
  userId: string;
  organizationId: string;
}) {
  const [page, setPage] = useState(1);
  const perPage = 10;

  const { data, isLoading } = useGetContainerComponentDetailQuery({
    containerKey,
    entityId,
    typeKey,
    userId,
    organizationId,
    page,
    perPage,
  });

  if (isLoading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="text.secondary">Loading history...</Typography>
      </Box>
    );
  }

  if (!data?.items?.length) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="text.secondary">
          No historical versions found.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 2 }}>
        All Versions ({data.total ?? data.items.length})
      </Typography>
      {data.items.map((version, index) => (
        <Box key={version.component_id ?? index} sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 1,
            }}
          >
            <Chip
              label={version.stale ? 'Stale' : 'Current'}
              color={version.stale ? 'default' : 'success'}
              size="small"
              variant="outlined"
            />
            <Typography variant="caption" color="text.secondary">
              Generated:{' '}
              {version.generated_at
                ? formatDateTime(version.generated_at)
                : 'N/A'}
            </Typography>
            {version.component_id && (
              <Typography variant="caption" color="text.secondary">
                ID: {version.component_id.slice(0, 8)}...
              </Typography>
            )}
          </Box>
          <Box
            component="pre"
            sx={{
              bgcolor: 'grey.900',
              color: 'grey.100',
              p: 2,
              borderRadius: 1,
              overflow: 'auto',
              maxHeight: 400,
              fontSize: '0.75rem',
              lineHeight: 1.5,
            }}
          >
            {JSON.stringify(version.data, null, 2)}
          </Box>
        </Box>
      ))}
      {(data.total_pages ?? 1) > 1 && (
        <Pagination
          count={data.total_pages ?? 1}
          page={page}
          onChange={(_, newPage) => setPage(newPage)}
          size="small"
          sx={{ mt: 1 }}
        />
      )}
    </Box>
  );
}

export default function ContainerEntityDetailPage() {
  const {
    providerId: urlProviderId,
    containerKey,
    entityId,
  } = useParams<{
    providerId: string;
    containerKey: string;
    entityId: string;
  }>();

  const providerId = urlProviderId ? decodeURIComponent(urlProviderId) : null;

  const [expandedComponents, setExpandedComponents] = useState<Set<string>>(
    new Set(),
  );
  const [historyComponents, setHistoryComponents] = useState<Set<string>>(
    new Set(),
  );
  const [layoutExpanded, setLayoutExpanded] = useState(false);
  const [regenerateResult, setRegenerateResult] = useState<string | null>(null);

  const { data: provider, isLoading: providerLoading } =
    useGetProviderInfoQuery(
      { providerId: providerId ?? '' },
      { skip: !providerId },
    );

  const {
    data: entityDetail,
    isLoading: entityLoading,
    isFetching,
    refetch,
  } = useGetContainerEntityDetailQuery(
    {
      containerKey: containerKey ?? '',
      entityId: entityId ?? '',
      userId: providerId ?? '',
      organizationId: provider?.organization_id ?? '',
    },
    {
      skip:
        !providerId ||
        !containerKey ||
        !entityId ||
        !provider?.organization_id,
    },
  );

  const [regenerate, { isLoading: regenerating }] =
    useRegeneratePatientIntelligenceMutation();

  const toggleComponent = useCallback((componentId: string) => {
    setExpandedComponents((prev) => {
      const next = new Set(prev);
      if (next.has(componentId)) {
        next.delete(componentId);
      } else {
        next.add(componentId);
      }
      return next;
    });
  }, []);

  const toggleHistory = useCallback((typeKey: string) => {
    setHistoryComponents((prev) => {
      const next = new Set(prev);
      if (next.has(typeKey)) {
        next.delete(typeKey);
      } else {
        next.add(typeKey);
      }
      return next;
    });
  }, []);

  const handleRegenerate = async () => {
    if (!entityId || !providerId || !provider?.organization_id) return;

    try {
      const result = await regenerate({
        regeneratePatientIntelligenceRequest: {
          patient_id: entityId,
          user_id: providerId,
          organization_id: provider.organization_id,
        },
      }).unwrap();
      setRegenerateResult(result.task_id);
    } catch {
      setRegenerateResult(null);
    }
  };

  if (!providerId || !containerKey || !entityId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">
              Missing parameters
            </h2>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  if (providerLoading || entityLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <LoadingState message="Loading container detail..." />
        </main>
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">
              Provider not found
            </h2>
            <Link to="/admin-v2">
              <Button className="mt-4">Back to Search</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  if (!entityDetail) {
    return (
      <div className="min-h-screen bg-gray-50">
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">
              Container not found
            </h2>
            <p className="text-gray-600 mt-2">
              No container layout exists for this entity.
            </p>
            <Link
              to={`/admin-v2/provider/${encodeURIComponent(providerId)}/containers/${containerKey}`}
            >
              <Button className="mt-4">Back to Entities</Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const encodedProviderId = encodeURIComponent(providerId);

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="flex mb-6" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <Link to="/admin-v2">
                <Button
                  variant="text"
                  className="text-sm font-medium text-gray-700 hover:text-blue-600"
                >
                  <OpenInNew className="mr-2 h-4 w-4" />
                  Search
                </Button>
              </Link>
            </li>
            <li>
              <div className="flex items-center">
                <ChevronRight className="h-4 w-4 text-gray-400 mx-2" />
                <Link to={`/admin-v2/provider/${encodedProviderId}`}>
                  <Button
                    variant="text"
                    className="text-sm font-medium text-gray-700 hover:text-blue-600"
                  >
                    {provider.name}
                  </Button>
                </Link>
              </div>
            </li>
            <li>
              <div className="flex items-center">
                <ChevronRight className="h-4 w-4 text-gray-400 mx-2" />
                <Link
                  to={`/admin-v2/provider/${encodedProviderId}/containers/${containerKey}`}
                >
                  <Button
                    variant="text"
                    className="text-sm font-medium text-gray-700 hover:text-blue-600"
                  >
                    Containers ({containerKey})
                  </Button>
                </Link>
              </div>
            </li>
            <li>
              <div className="flex items-center">
                <ChevronRight className="h-4 w-4 text-gray-400 mx-2" />
                <span className="ml-1 text-sm font-medium text-gray-500 md:ml-2">
                  {entityId.slice(0, 8)}...
                </span>
              </div>
            </li>
          </ol>
        </nav>

        {/* Header Card */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="h-16 w-16 rounded-full bg-purple-100 flex items-center justify-center">
                  <Inventory2 className="h-8 w-8 text-purple-600" />
                </div>
                <div className="ml-6">
                  <h1 className="text-2xl font-bold text-gray-900">
                    Container Detail
                  </h1>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-sm text-gray-500">Entity ID:</span>
                    <span className="text-sm font-mono text-gray-700">
                      {entityId}
                    </span>
                    <Tooltip title="Copy Entity ID">
                      <IconButton
                        size="small"
                        onClick={() =>
                          void navigator.clipboard.writeText(entityId)
                        }
                      >
                        <ContentCopy fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </div>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-sm text-gray-500">
                      Container: {entityDetail.container_key}
                    </span>
                    <span className="text-sm text-gray-500">
                      Assembled:{' '}
                      {entityDetail.assembled_at
                        ? formatDateTime(entityDetail.assembled_at)
                        : 'Never'}
                    </span>
                    <Chip
                      label={entityDetail.stale ? 'Stale' : 'Current'}
                      color={entityDetail.stale ? 'warning' : 'success'}
                      size="small"
                      variant="outlined"
                    />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={() => void refetch()}
                  disabled={isFetching}
                  size="small"
                >
                  {isFetching ? 'Refreshing...' : 'Refresh'}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AutoFixHigh />}
                  onClick={() => void handleRegenerate()}
                  disabled={regenerating}
                  size="small"
                >
                  {regenerating ? 'Regenerating...' : 'Regenerate'}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Regenerate Result */}
        {regenerateResult && (
          <Alert severity="info" sx={{ mb: 3 }} onClose={() => setRegenerateResult(null)}>
            Regeneration task enqueued. Task ID:{' '}
            <code>{regenerateResult}</code>. The container will update after
            the background task completes. Use the Refresh button to re-fetch.
          </Alert>
        )}

        {/* Layout Section */}
        <Card sx={{ mb: 3 }}>
          <CardHeader
            title={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
                onClick={() => setLayoutExpanded(!layoutExpanded)}
              >
                <Typography variant="h6" sx={{ flex: 1 }}>
                  Layout Structure
                </Typography>
                {layoutExpanded ? <ExpandLess /> : <ExpandMore />}
              </Box>
            }
          />
          <Collapse in={layoutExpanded}>
            <CardContent>
              <Box
                component="pre"
                sx={{
                  bgcolor: 'grey.900',
                  color: 'grey.100',
                  p: 2,
                  borderRadius: 1,
                  overflow: 'auto',
                  maxHeight: 600,
                  fontSize: '0.8rem',
                  lineHeight: 1.5,
                }}
              >
                {JSON.stringify(entityDetail.layout, null, 2)}
              </Box>
            </CardContent>
          </Collapse>
        </Card>

        {/* Components — rendered in layout order */}
        {(() => {
          // Build lookup: component_id → stored component data
          const componentsById = new Map(
            entityDetail.components
              .filter((c) => c.component_id)
              .map((c) => [c.component_id!, c]),
          );

          const layout = entityDetail.layout as {
            top_level?: { type_key: string; component_id?: string; delivery?: string }[];
            sections?: {
              id?: string;
              title?: string;
              subtitle?: string | null;
              components?: { type_key: string; component_id?: string; delivery?: string }[];
            }[];
          };

          const renderLayoutEntry = (
            entry: { type_key: string; component_id?: string; delivery?: string },
            index: number,
          ) => {
            const stored = entry.component_id
              ? componentsById.get(entry.component_id)
              : undefined;

            if (!stored) {
              // Deterministic component — no stored data
              return (
                <Card key={`${entry.type_key}-${index}`} sx={{ mb: 1.5 }}>
                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Typography variant="subtitle2" color="text.secondary">
                      {entry.type_key}
                    </Typography>
                    <Chip label="Deterministic" size="small" variant="outlined" />
                    {entry.delivery && (
                      <Typography variant="caption" color="text.secondary">
                        {entry.delivery}
                      </Typography>
                    )}
                  </Box>
                </Card>
              );
            }

            // Stored component with data
            const componentKey = stored.component_id || stored.type_key;
            const isExpanded = expandedComponents.has(componentKey);

            return (
              <Card key={componentKey} sx={{ mb: 1.5 }}>
                <CardHeader
                  sx={{ pb: isExpanded ? 0 : 1 , '&:last-child': { pb: isExpanded ? 0 : 1 } }}
                  title={
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                      }}
                      onClick={() => toggleComponent(componentKey)}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                            {stored.type_key}
                          </Typography>
                          <Chip
                            label={stored.stale ? 'Stale' : 'Current'}
                            color={stored.stale ? 'warning' : 'success'}
                            size="small"
                            variant="outlined"
                          />
                          {entry.delivery && (
                            <Typography variant="caption" color="text.secondary">
                              {entry.delivery}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                          {stored.component_id && (
                            <Typography variant="caption" color="text.secondary">
                              ID: {stored.component_id.slice(0, 8)}...
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            Generated:{' '}
                            {stored.generated_at
                              ? formatDateTime(stored.generated_at)
                              : 'N/A'}
                          </Typography>
                        </Box>
                      </Box>
                      {isExpanded ? <ExpandLess /> : <ExpandMore />}
                    </Box>
                  }
                />
                <Collapse in={isExpanded}>
                  <CardContent>
                    <Box
                      component="pre"
                      sx={{
                        bgcolor: 'grey.900',
                        color: 'grey.100',
                        p: 2,
                        borderRadius: 1,
                        overflow: 'auto',
                        maxHeight: 500,
                        fontSize: '0.8rem',
                        lineHeight: 1.5,
                      }}
                    >
                      {JSON.stringify(stored.data, null, 2)}
                    </Box>
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<History />}
                      onClick={() => toggleHistory(stored.type_key)}
                      sx={{ mt: 1 }}
                    >
                      {historyComponents.has(stored.type_key)
                        ? 'Hide History'
                        : 'View History'}
                    </Button>
                    {historyComponents.has(stored.type_key) && (
                      <>
                        <Divider sx={{ my: 1 }} />
                        <ComponentHistory
                          containerKey={containerKey!}
                          entityId={entityId!}
                          typeKey={stored.type_key}
                          userId={providerId!}
                          organizationId={provider!.organization_id!}
                        />
                      </>
                    )}
                  </CardContent>
                </Collapse>
              </Card>
            );
          };

          return (
            <>
              {/* Top-level components */}
              {layout.top_level && layout.top_level.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1.5 }}>
                    Top Level
                  </Typography>
                  {layout.top_level.map(renderLayoutEntry)}
                </Box>
              )}

              {/* Sections */}
              {layout.sections?.map((section) => (
                <Box key={section.id ?? section.title} sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 0.5 }}>
                    {section.title ?? section.id ?? 'Untitled Section'}
                  </Typography>
                  {section.subtitle && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {section.subtitle}
                    </Typography>
                  )}
                  {section.id && (
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
                      Section ID: {section.id}
                    </Typography>
                  )}
                  {section.components?.length ? (
                    section.components.map(renderLayoutEntry)
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No components in this section.
                    </Typography>
                  )}
                </Box>
              ))}
            </>
          );
        })()}
      </main>
    </div>
  );
}
